/**
 * Role-based shift configuration
 * Defines the pre-shift wizard steps and checklist items for each role
 */

export type StepType =
  | 'welcome'
  | 'menu'
  | 'allergies'
  | 'section'
  | 'reservations'
  | 'kitchen'
  | 'overview'
  | 'start';

export type ChecklistItem =
  | 'menuReviewed'
  | 'allergiesReviewed'
  | 'sectionConfirmed'
  | 'reservationsReviewed'
  | 'eightySixReviewed'
  | 'stationConfirmed'
  | 'overviewReviewed';

export interface RoleShiftConfig {
  steps: StepType[];
  requiresSection: boolean;
  checklistItems: ChecklistItem[];
}

export const ROLE_SHIFT_CONFIGS: Record<string, RoleShiftConfig> = {
  server: {
    steps: ['welcome', 'menu', 'allergies', 'section', 'start'],
    requiresSection: true,
    checklistItems: ['menuReviewed', 'allergiesReviewed', 'sectionConfirmed'],
  },
  bartender: {
    steps: ['welcome', 'menu', 'allergies', 'section', 'start'],
    requiresSection: true,
    checklistItems: ['menuReviewed', 'allergiesReviewed', 'sectionConfirmed'],
  },
  host: {
    steps: ['welcome', 'reservations', 'start'],
    requiresSection: false,
    checklistItems: ['reservationsReviewed'],
  },
  cook: {
    steps: ['welcome', 'kitchen', 'start'],
    requiresSection: false,
    checklistItems: ['eightySixReviewed', 'stationConfirmed'],
  },
  manager: {
    steps: ['welcome', 'overview', 'start'],
    requiresSection: false,
    checklistItems: ['overviewReviewed'],
  },
  busser: {
    steps: ['welcome', 'section', 'start'],
    requiresSection: true,
    checklistItems: ['sectionConfirmed'],
  },
  dishwasher: {
    steps: ['welcome', 'start'],
    requiresSection: false,
    checklistItems: [],
  },
};

/**
 * Get the shift configuration for a given role.
 * Exact match first, then fuzzy ("line cook" → cook, "head bartender" →
 * bartender) — a blind fallback to server used to hand kitchen roles the
 * floor-section wizard and vice versa. Server remains the last resort.
 */
export const getRoleShiftConfig = (role: string): RoleShiftConfig => {
  const normalizedRole = (role || '').toLowerCase().trim();
  if (ROLE_SHIFT_CONFIGS[normalizedRole]) return ROLE_SHIFT_CONFIGS[normalizedRole];
  const fuzzy: Array<[RegExp, string]> = [
    [/cook|chef|kitchen|grill|fry|prep/, 'cook'],
    [/bartend|\bbar\b/, 'bartender'],
    [/host|greeter/, 'host'],
    [/buss|bus boy/, 'busser'],
    [/dish/, 'dishwasher'],
    [/manag|lead|super/, 'manager'],
    [/serv|wait/, 'server'],
  ];
  for (const [re, key] of fuzzy) if (re.test(normalizedRole)) return ROLE_SHIFT_CONFIGS[key];
  return ROLE_SHIFT_CONFIGS.server;
};

/**
 * Get all checklist items that need to be completed for a role
 */
export const getRequiredChecklistItems = (role: string): ChecklistItem[] => {
  return getRoleShiftConfig(role).checklistItems;
};

/**
 * Check if a role requires section assignment
 */
export const roleRequiresSection = (role: string): boolean => {
  return getRoleShiftConfig(role).requiresSection;
};
